import logger from '../../utils/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera } from 'lucide-react';
import PatientAvatar from './PatientAvatar';
import '../../style/ProfilePictureCard.css';

const BASE_URL = process.env.REACT_APP_API_URL;

// Final stored image is a square; 512px keeps faces crisp while staying tiny
// (~20-80KB) once encoded. We compress client-side so the backend needs no
// image library.
const OUTPUT_SIZE = 512;
const ENCODE_QUALITY = 0.85;

/**
 * Pick the best canvas encoding the browser actually supports. WebP gives the
 * smallest file for the same quality; Safari historically can't encode WebP via
 * canvas, so we fall back to JPEG (the backend accepts webp/jpeg/png).
 */
function pickEncoding() {
  const c = document.createElement('canvas');
  const webp = c.toDataURL('image/webp');
  return webp.startsWith('data:image/webp')
    ? { mime: 'image/webp', ext: 'webp' }
    : { mime: 'image/jpeg', ext: 'jpg' };
}

/**
 * Profile picture card for the patient About page.
 *
 * Shows the patient's current photo (or a gray placeholder). Hovering the avatar
 * reveals a camera overlay; clicking it opens a webcam capture modal with a live
 * preview, capture/retake, and save. The captured frame is center-cropped to a
 * square and downscaled before upload.
 *
 * Requires a secure context (HTTPS or localhost) for camera access.
 *
 * Props: risPatientsId, user, type, onUpdated(url)
 */
const ProfilePictureCard = ({ risPatientsId, user, type, onUpdated }) => {
  const [url, setUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [captureState, setCaptureState] = useState('idle'); // idle | live | captured
  const [capturedPreview, setCapturedPreview] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Load the current photo (signed URL, 10-min expiry) on mount / patient change.
  const fetchPicture = useCallback(async () => {
    if (!risPatientsId) return;
    try {
      const res = await fetch(`${BASE_URL}/patients/profilePicture?ris_patients_id=${risPatientsId}`);
      if (res.ok) {
        const data = await res.json();
        setUrl(data.url || null);
      }
    } catch (err) {
      logger.error('Error fetching profile picture:', err);
    }
  }, [risPatientsId]);

  useEffect(() => { fetchPicture(); }, [fetchPicture]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('This browser does not support camera capture.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCaptureState('live');
    } catch (err) {
      const msg =
        err.name === 'NotAllowedError' || err.name === 'SecurityError'
          ? 'Camera access was blocked. Allow camera permission in your browser and try again.'
          : err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError'
          ? 'No camera was found on this device.'
          : 'Unable to access the camera. Check your device and browser settings.';
      setCameraError(msg);
      setCaptureState('idle');
    }
  }, []);

  // Start the camera when the modal opens; always release it when it closes.
  useEffect(() => {
    if (!modalOpen) return;
    startCamera();
    return () => stopCamera();
  }, [modalOpen, startCamera, stopCamera]);

  // Stop the camera if the component unmounts while the modal is open.
  useEffect(() => stopCamera, [stopCamera]);

  // Keep the video element bound to the live stream after re-renders (retake).
  useEffect(() => {
    if (captureState === 'live' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [captureState]);

  const openModal = () => {
    if (!risPatientsId) return;
    setSaveError(null);
    setCapturedPreview(null);
    setCaptureState('idle');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCapturedPreview(null);
    setCaptureState('idle');
    setSaveError(null);
  };

  // Draw the current video frame center-cropped to a square OUTPUT_SIZE canvas.
  const drawToCanvas = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return false;
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const side = Math.min(vw, vh);
    const sx = (vw - side) / 2;
    const sy = (vh - side) / 2;
    ctx.drawImage(video, sx, sy, side, side, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    return true;
  };

  const handleCapture = () => {
    if (!drawToCanvas()) return;
    const { mime } = pickEncoding();
    setCapturedPreview(canvasRef.current.toDataURL(mime, ENCODE_QUALITY));
    setCaptureState('captured');
  };

  const handleRetake = () => {
    setCapturedPreview(null);
    setSaveError(null);
    setCaptureState('live');
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    setSaveError(null);
    const { mime, ext } = pickEncoding();
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setSaving(false);
        setSaveError('Could not process the captured image. Please retake.');
        return;
      }
      try {
        const form = new FormData();
        form.append('image', blob, `profile.${ext}`);
        // Do NOT set Content-Type — the browser adds the multipart boundary, and
        // the global authFetch interceptor attaches the auth token.
        const res = await fetch(
          `${BASE_URL}/patients/profilePicture?ris_patients_id=${risPatientsId}&user=${user}&type=${type}`,
          { method: 'POST', body: form }
        );
        if (res.ok) {
          const data = await res.json();
          const newUrl = data.url || null;
          setUrl(newUrl);
          if (!newUrl) fetchPicture();
          if (onUpdated) onUpdated(newUrl);
          closeModal();
        } else {
          setSaveError('Failed to save photo. Please try again.');
        }
      } catch (err) {
        logger.error('Error saving profile picture:', err);
        setSaveError('Failed to save photo. Please try again.');
      } finally {
        setSaving(false);
      }
    }, mime, ENCODE_QUALITY);
  };

  return (
    <div className="ppic-card">
      <button
        type="button"
        className="ppic-avatar-wrap"
        onClick={openModal}
        disabled={!risPatientsId}
        title={risPatientsId ? (url ? 'Update profile picture' : 'Add profile picture') : 'Register the patient to add a photo'}
      >
        <PatientAvatar url={url} size={160} alt="Patient profile picture" />
        {risPatientsId && (
          <span className="ppic-hover-overlay">
            <Camera className="ppic-hover-icon" size={38} strokeWidth={2} />
            <span className="ppic-hover-text">{url ? 'Update photo' : 'Add photo'}</span>
          </span>
        )}
      </button>
      <span className="ppic-caption">Profile Picture</span>

      {modalOpen && (
        <div className="ppic-modal-overlay" onClick={closeModal}>
          <div className="ppic-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ppic-modal-header">
              <h3>{url ? 'Update Profile Picture' : 'Add Profile Picture'}</h3>
              <button className="ppic-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>

            <div className="ppic-modal-body">
              {cameraError ? (
                <div className="ppic-error">
                  <p>{cameraError}</p>
                  <button className="ppic-btn primary" onClick={startCamera}>Try Again</button>
                </div>
              ) : (
                <div className="ppic-stage">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="ppic-video"
                    style={{ display: captureState === 'captured' ? 'none' : 'block' }}
                  />
                  {captureState === 'captured' && capturedPreview && (
                    <img src={capturedPreview} alt="Captured preview" className="ppic-preview" />
                  )}
                  <span className="ppic-frame-guide" aria-hidden="true" />
                  {captureState === 'idle' && (
                    <span className="ppic-stage-hint">Starting camera…</span>
                  )}
                </div>
              )}
              {saveError && <p className="ppic-inline-error">{saveError}</p>}
            </div>

            {!cameraError && (
              <div className="ppic-modal-actions">
                {captureState === 'captured' ? (
                  <>
                    <button className="ppic-btn secondary" onClick={handleRetake} disabled={saving}>
                      Retake
                    </button>
                    <button className="ppic-btn primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Photo'}
                    </button>
                  </>
                ) : (
                  <button className="ppic-btn primary" onClick={handleCapture} disabled={captureState !== 'live'}>
                    <Camera size={16} /> Capture
                  </button>
                )}
              </div>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureCard;
